// ── The pane that draws a PDF, page by page ─────────────────────────────────────────────────────
// Lifted out of FileLibrary/index.tsx so the file viewer tab (/m/file/:id, FileViewer) draws a
// document with exactly the pane Filing draws it in — one copy, so the two can never drift apart.
// FileLibrary re-exports it, which is how Drop File's AI pop-up still reaches it; Chat imports it from here,
// together with the page-drawing call below, which it draws a PDF bubble's first page with.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// pdf.js on demand, rendered straight to a canvas — same worker + render pattern as
// DueDiligence/index.tsx and BankStatement/SlipCell.tsx. A plain <iframe src="blob:...">
// hands the file to Chrome's own PDF viewer, which ships its own toolbar + page-thumbnail
// sidebar that the app cannot suppress via URL fragments in modern Chrome (Calem, re-opened
// report: wanted that sidebar gone so the document fills the full pane width). Rendering each
// page ourselves means there is no viewer chrome to begin with.
let pdfLibP: Promise<any> | null = null;
function getPdfLib(): Promise<any> {
  if (!pdfLibP) {
    pdfLibP = import('pdfjs-dist/legacy/build/pdf').then((m: any) => {
      const lib = m.default || m;
      lib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
      return lib;
    });
  }
  return pdfLibP;
}
/** One page of a PDF drawn as a picture, and how many pages the document has. Exported for the Chat window, which
 *  draws page 1 of a PDF sent in a chat inside its bubble (report 6cc0de58, opening #3: "I want the PDF to show a
 *  preview in the chat rather than just a link") — the same renderer this pane uses, so the two can never differ. */
export async function pdfPageDataUrl(data: ArrayBuffer, pageNo: number, targetW: number): Promise<{ url: string; pages: number }> {
  const pdfjs = await getPdfLib();
  const doc = await pdfjs.getDocument({ data: data.slice(0) }).promise;
  const page = await doc.getPage(pageNo);
  const base = page.getViewport({ scale: 1 });
  const scale = Math.min(4, Math.max(0.2, (targetW * 2) / base.width));
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  await page.render({ canvasContext: ctx, viewport }).promise;
  const url = canvas.toDataURL('image/jpeg', 0.9);
  const pages = doc.numPages;
  try { doc.destroy(); } catch { /* best effort */ }
  return { url, pages };
}

// PDF branch of the preview pane: every page rendered full pane-width and stacked, scrollable —
// no thumbnail rail, no outline, no toolbar. `src` is the blob: URL the caller already holds for
// this file; its lifetime (revoke on close / on picking another file) is managed by the caller.
// Also Drop File's AI pop-up (DropProposals) shows the dropped PDF with it, and the file viewer
// tab a Job Documents paperclip opens (FileViewer).
export function PdfPreview({ src }: { src: string }) {
  const { t } = useTranslation();
  const [pages, setPages] = useState<string[]>([]);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    setPages([]); setFailed(false);
    (async () => {
      try {
        const buf = await (await fetch(src)).arrayBuffer();
        if (!live) return;
        const first = await pdfPageDataUrl(buf, 1, 1400);
        if (!live) return;
        const urls = [first.url];
        setPages(urls);
        // Remaining pages stream in behind the first — the reader sees page 1 immediately.
        for (let n = 2; n <= first.pages; n++) {
          const r = await pdfPageDataUrl(buf, n, 1400);
          if (!live) return;
          urls.push(r.url); setPages([...urls]);
        }
      } catch { if (live) setFailed(true); }
    })();
    return () => { live = false; };
  }, [src]);
  if (failed) return (
    <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
      <i className="ti ti-file-alert" style={{ fontSize: 28, display: 'block', marginBottom: 8 }} />{t('fl_preview_fail')}
    </div>
  );
  if (!pages.length) return <i className="ti ti-loader" style={{ fontSize: 22, color: 'var(--text-tertiary)', display: 'inline-block', animation: 'spin 1s linear infinite' }} />;
  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {pages.map((u, i) => <img key={i} src={u} alt="" style={{ width: '100%', display: 'block', borderRadius: 4, background: '#fff' }} />)}
    </div>
  );
}
